import { useLayoutEffect, useRef } from 'react';
import type { Field, FieldType } from '../../api/types';

const inputTypeFor: Record<FieldType, string> = {
  text: 'text',
  textArea: 'text',
  email: 'email',
  tel: 'tel',
  date: 'date',
  time: 'time',
  url: 'url',
  select: 'text',
  checkbox: 'checkbox',
};

interface FieldControlProps {
  field: Field;
  value: string;
  errors: string[];

  /** True when the checkbox named by the field's `disabledWhen` is ticked. */
  disabled: boolean;

  /**
   * Ghost text to show instead of the field's own placeholder — set when a chosen option was meant
   * to fill this control and had nothing to put in it. Null the rest of the time.
   */
  placeholderOverride: string | null;

  /** Makes the control span the whole grid row instead of taking one column of it. */
  fullRow: boolean;

  onChange: (key: string, value: string) => void;
}

/**
 * One form control, rendered entirely from its manifest description. Adding a field to a template
 * requires no change here.
 */
export function FieldControl({
  field,
  value,
  errors,
  disabled,
  placeholderOverride,
  fullRow,
  onChange,
}: FieldControlProps) {
  const id = `field-${field.key}`;
  const showErrors = errors.length > 0 && !disabled;
  const describedBy = showErrors ? `-error` : undefined;
  const placeholder = placeholderOverride ?? field.placeholder ?? undefined;

  // A checkbox carries its label to the right of the box, so it gets its own layout rather than the
  // label-above-control shape everything else uses.
  if (field.type === 'checkbox') {
    return (
      <div className={classNames('field', 'field--checkbox', disabled && 'field--disabled', fullRow && 'field--wide')}>
        <label className="checkbox" htmlFor={id}>
          <input
            id={id}
            type="checkbox"
            checked={value === 'true'}
            /* A checkbox can be gated too: "split shift" means nothing on a day marked closed. */
            disabled={disabled}
            onChange={(e) => onChange(field.key, e.target.checked ? 'true' : '')}
          />
          <span>{field.label}</span>
        </label>
      </div>
    );
  }

  const shared = {
    id,
    value,
    disabled,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      onChange(field.key, e.target.value),
    'aria-invalid': showErrors || undefined,
    'aria-describedby': describedBy,
    className: showErrors ? 'control control--invalid' : 'control',
  };

  // A multi-line note squeezed into one column of a grid built for date pickers is unusable, so it
  // takes the whole row wherever it appears. A field the manifest gives a span to takes that many
  // columns instead — enough room for a long value without pushing its neighbours off the row.
  const wide = fullRow || field.type === 'textArea';
  const spans = !wide && field.columnSpan !== null && field.columnSpan > 1;

  return (
    <div
      className={classNames('field', disabled && 'field--disabled', wide && 'field--wide', spans && 'field--span')}
      /* A custom property rather than an inline grid-column, so the narrow-screen media query can
         still put the control back on a row of its own — an inline style would win over it. */
      style={spans ? ({ '--span': field.columnSpan } as React.CSSProperties) : undefined}
    >
      <label className="field__label" htmlFor={id}>
        {field.label}
        {field.required && !disabled && (
          <span className="field__required" aria-label="required">
            {' '}
            *
          </span>
        )}
      </label>

      {field.type === 'textArea' ? (
        <AutoSizingTextArea
          {...shared}
          placeholder={placeholder}
          maxLength={field.maxLength ?? undefined}
        />
      ) : field.type === 'select' ? (
        <select {...shared}>
          <option value="">Choose…</option>
          {field.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          {...shared}
          type={inputTypeFor[field.type]}
          placeholder={placeholder}
          maxLength={field.maxLength ?? undefined}
        />
      )}

      {showErrors && (
        <p className="field__error" id={`${id}-error`} role="alert">
          {errors.join(' ')}
        </p>
      )}
    </div>
  );
}

function classNames(...parts: (string | false | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}

/**
 * A textarea that starts the height of a single-line input and grows as it is typed into.
 *
 * A fixed `rows` is wrong in both directions: three rows of empty box for the sender who leaves it
 * blank, and a scrollbar inside a small window for the one who writes real directions. `field-sizing`
 * would do this in CSS but is Chromium-only, so the height is measured instead — reset to `auto`
 * first, because `scrollHeight` on an element that is already tall enough only ever reports the
 * current height and the box could never shrink again.
 */
function AutoSizingTextArea(props: React.ComponentPropsWithoutRef<'textarea'>) {
  const ref = useRef<HTMLTextAreaElement>(null);

  // Layout effect rather than an effect: this runs before paint, so the box is never briefly the
  // wrong height when a value arrives from a prefill or a reset.
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight}px`;
  }, [props.value]);

  return <textarea {...props} ref={ref} rows={1} />;
}
